import cssVariables from "../css/variables.module.scss";
import { SchemadrawElement, FontFamilyValues } from "../element/types";
import { COLOR_PALETTE } from "./colors";
import { AppProps } from "./types";

export const isDarwin = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
export const isWindows = /^Win/.test(navigator.platform);
export const isAndroid = /\b(android)\b/i.test(navigator.userAgent);
export const isFirefox =
  "netscape" in window &&
  navigator.userAgent.indexOf("rv:") > 1 &&
  navigator.userAgent.indexOf("Gecko") > 1;
export const isChrome = navigator.userAgent.indexOf("Chrome") !== -1;
export const isSafari =
  !isChrome && navigator.userAgent.indexOf("Safari") !== -1;
export const isIOS =
  /iPad|iPhone/.test(navigator.platform) ||
  // iPadOS 13+
  (navigator.userAgent.includes("Mac") && "ontouchend" in document);
// keeping function so it can be mocked in test
export const isBrave = () =>
  (navigator as any).brave?.isBrave?.name === "isBrave";

export const supportsResizeObserver =
  typeof window !== "undefined" && "ResizeObserver" in window;


export const APP_NAME = "Procviz";

export const TEXT_AUTOWRAP_THRESHOLD = 36; // px
export const DRAGGING_THRESHOLD = 10; // px
export const LINE_CONFIRM_THRESHOLD = 8; // px
export const ELEMENT_SHIFT_TRANSLATE_AMOUNT = 5;
export const ELEMENT_TRANSLATE_AMOUNT = 1;
export const TEXT_TO_CENTER_SNAP_THRESHOLD = 30;
export const SHIFT_LOCKING_ANGLE = Math.PI / 12;
export const DEFAULT_LASER_COLOR = "red";
export const CURSOR_TYPE = {
  TEXT: "text",
  CROSSHAIR: "crosshair",
  GRABBING: "grabbing",
  GRAB: "grab",
  POINTER: "pointer",
  MOVE: "move",
  AUTO: "",
};
export const POINTER_BUTTON = {
  MAIN: 0,
  WHEEL: 1,
  SECONDARY: 2,
  TOUCH: -1,
  ERASER: 5,
} as const;

export const POINTER_EVENTS = {
  enabled: "all",
  disabled: "none",
  // asserted as any so it can be freely assigned to React Element
  // "pointerEnvets" CSS prop
  inheritFromUI: "var(--ui-pointerEvents)" as any,
} as const;

export enum EVENT {
  COPY = "copy",
  PASTE = "paste",
  CUT = "cut",
  KEYDOWN = "keydown",
  KEYUP = "keyup",
  MOUSE_MOVE = "mousemove",
  RESIZE = "resize",
  UNLOAD = "unload",
  FOCUS = "focus",
  BLUR = "blur",
  DRAG_OVER = "dragover",
  DROP = "drop",
  GESTURE_END = "gestureend",
  BEFORE_UNLOAD = "beforeunload",
  GESTURE_START = "gesturestart",
  GESTURE_CHANGE = "gesturechange",
  POINTER_MOVE = "pointermove",
  POINTER_DOWN = "pointerdown",
  POINTER_UP = "pointerup",
  STATE_CHANGE = "statechange",
  WHEEL = "wheel",
  TOUCH_START = "touchstart",
  TOUCH_END = "touchend",
  HASHCHANGE = "hashchange",
  VISIBILITY_CHANGE = "visibilitychange",
  SCROLL = "scroll",
  // custom events
  SCHEMADRAW_LINK = "schemadraw-link",
  MENU_ITEM_SELECT = "menu.itemSelect",
  MESSAGE = "message",
  FULLSCREENCHANGE = "fullscreenchange",
}

export const YOUTUBE_STATES = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

export const ENV = {
  TEST: "test",
  DEVELOPMENT: "development",
};

export const CLASSES = {
  SHAPE_ACTIONS_MENU: "App-menu__left",
};

// 1-based in case we ever do `if(element.fontFamily)`
export const FONT_FAMILY = {
  Virgil: 1,
  Helvetica: 2,
  Cascadia: 3,
};

export const THEME = {
  LIGHT: "light",
  DARK: "dark",
};

export const FRAME_STYLE = {
  strokeColor: "#bbb" as SchemadrawElement["strokeColor"],
  strokeWidth: 2 as SchemadrawElement["strokeWidth"],
  strokeStyle: "solid" as SchemadrawElement["strokeStyle"],
  fillStyle: "solid" as SchemadrawElement["fillStyle"],
  roughness: 0 as SchemadrawElement["roughness"],
  roundness: null as SchemadrawElement["roundness"],
  backgroundColor: "transparent" as SchemadrawElement["backgroundColor"],
  radius: 8,
  nameOffsetY: 3,
  nameColorLightTheme: "#999999",
  nameColorDarkTheme: "#7a7a7a",
  nameFontSize: 14,
  nameLineHeight: 1.25,
};

export const WINDOWS_EMOJI_FALLBACK_FONT = "Segoe UI Emoji";

export const MIN_FONT_SIZE = 1;
export const DEFAULT_FONT_SIZE = 20;
export const DEFAULT_FONT_FAMILY: FontFamilyValues = FONT_FAMILY.Helvetica;
export const DEFAULT_TEXT_ALIGN = "left";
export const DEFAULT_VERTICAL_ALIGN = "top";
export const DEFAULT_VERSION = "{version}";
export const DEFAULT_TRANSFORM_HANDLE_SPACING = 2;

export const SIDE_RESIZING_THRESHOLD = 2 * DEFAULT_TRANSFORM_HANDLE_SPACING;
// a small epsilon to make side resizing always take precedence
// (avoids an increase in renders and changes to tests)
const EPSILON = 0.00001;
export const DEFAULT_COLLISION_THRESHOLD =
  2 * SIDE_RESIZING_THRESHOLD - EPSILON;

export const COLOR_WHITE = "#ffffff";
export const COLOR_CHARCOAL_BLACK = "#1e1e1e";
// keep this in sync with CSS
export const COLOR_VOICE_CALL = "#a2f1a6";

export const CANVAS_ONLY_ACTIONS = ["selectAll"];

export const GRID_SIZE = 20; // TODO make it configurable?

export const IMAGE_MIME_TYPES = {
  svg: "image/svg+xml",
  png: "image/png",
  jpg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  ico: "image/x-icon",
  avif: "image/avif",
  jfif: "image/jfif",
} as const;

export const MIME_TYPES = {
  json: "application/json",
  // schemadraw data
  schemadraw: "application/vnd.schemadraw+json",
  schemadrawlib: "application/vnd.schemadrawlib+json",
  // image-encoded schemadraw data
  "schemadraw.svg": "image/svg+xml",
  "schemadraw.png": "image/png",
  // binary
  binary: "application/octet-stream",
  // image
  ...IMAGE_MIME_TYPES,
} as const;

export const EXPORT_IMAGE_TYPES = {
  png: "png",
  svg: "svg",
  clipboard: "clipboard",
} as const;

export const EXPORT_DATA_TYPES = {
  schemadraw: "schemadraw",
  schemadrawClipboard: "schemadraw/clipboard",
  schemadrawLibrary: "schemadrawlib",
  schemadrawClipboardWithAPI: "schemadraw-api/clipboard",
} as const;

export const EXPORT_SOURCE =
  window.SCHEMADRAW_EXPORT_SOURCE || window.location.origin;

// time in milliseconds
export const IMAGE_RENDER_TIMEOUT = 500;
export const TAP_TWICE_TIMEOUT = 300;
export const TOUCH_CTX_MENU_TIMEOUT = 500;
export const CONTENT_TIMEOUT = 5000;
export const VERSION_TIMEOUT = 30000;
export const SCROLL_TIMEOUT = 100;
export const ZOOM_STEP = 0.1;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 30.0;
export const HYPERLINK_TOOLTIP_DELAY = 300;

// Report a user inactive after IDLE_THRESHOLD milliseconds
export const IDLE_THRESHOLD = 60_000;
// Report a user active each ACTIVE_THRESHOLD milliseconds
export const ACTIVE_THRESHOLD = 3_000;

export const THEME_FILTER = cssVariables.themeFilter;

export const URL_QUERY_KEYS = {
  addLibrary: "lid",
} as const;

export const URL_HASH_KEYS = {
  addLibrary: "lid",
} as const;

export const DEFAULT_UI_OPTIONS: AppProps["UIOptions"] = {
  canvasActions: {
    changeViewBackgroundColor: true,
    clearCanvas: true,
    export: { saveFileToDisk: true },
    loadScene: true,
    saveToActiveFile: true,
    toggleTheme: null,
    saveAsImage: true,
  },
  welcomeScreen: true,
};

// breakpoints
// -----------------------------------------------------------------------------
// sm screen
export const MQ_SM_MAX_WIDTH = 640;
// md screen
export const MQ_MAX_WIDTH_PORTRAIT = 730;
export const MQ_MAX_WIDTH_LANDSCAPE = 1000;
export const MQ_MAX_HEIGHT_LANDSCAPE = 500;
// sidebar
export const MQ_RIGHT_SIDEBAR_MIN_WIDTH = 1229;
// -----------------------------------------------------------------------------

export const LIBRARY_SIDEBAR_WIDTH = parseInt(cssVariables.rightSidebarWidth);

export const MAX_DECIMALS_FOR_SVG_EXPORT = 2;

export const EXPORT_SCALES = [1, 2, 3];
export const DEFAULT_EXPORT_PADDING = 10; // px

export const DEFAULT_MAX_IMAGE_WIDTH_OR_HEIGHT = 1440;

export const ALLOWED_IMAGE_MIME_TYPES = [
  MIME_TYPES.png,
  MIME_TYPES.jpg,
  MIME_TYPES.svg,
  MIME_TYPES.gif,
  MIME_TYPES.webp,
  MIME_TYPES.bmp,
  MIME_TYPES.ico,
] as const;

export const MAX_ALLOWED_FILE_BYTES = 2 * 1024 * 1024;

export const SVG_NS = "http://www.w3.org/2000/svg";

export const ENCRYPTION_KEY_BITS = 128;

export const VERSIONS = {
  schemadraw: 2,
  schemadrawLibrary: 2,
} as const;

export const BOUND_TEXT_PADDING = 5;
export const ARROW_LABEL_WIDTH_FRACTION = 0.7;
export const ARROW_LABEL_FONT_SIZE_TO_MIN_WIDTH_RATIO = 11;

export const VERTICAL_ALIGN = {
  TOP: "top",
  MIDDLE: "middle",
  BOTTOM: "bottom",
};

export const TEXT_ALIGN = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
};

export const ELEMENT_READY_TO_ERASE_OPACITY = 20;

// Radius represented as 25% of element's largest side (width/height).
// Used for LEGACY and PROPORTIONAL_RADIUS algorithms, or when the element is
// below the cutoff size.
export const DEFAULT_PROPORTIONAL_RADIUS = 0.25;
// Fixed radius for the ADAPTIVE_RADIUS algorithm. In pixels.
export const DEFAULT_ADAPTIVE_RADIUS = 32;
// roundness type (algorithm)
export const ROUNDNESS = {
  // Used for legacy rounding (rectangles), which currently works the same
  // as PROPORTIONAL_RADIUS, but we need to differentiate for UI purposes and
  // forwards-compat.
  LEGACY: 1,

  // Used for linear elements & diamonds
  PROPORTIONAL_RADIUS: 2,

  // Current default algorithm for rectangles, using fixed pixel radius.
  // It's working similarly to a regular border-radius, but attemps to make
  // radius visually similar across differnt element sizes, especially
  // very large and very small elements.
  //
  // NOTE right now we don't allow configuration and use a constant radius
  // (see DEFAULT_ADAPTIVE_RADIUS constant)
  ADAPTIVE_RADIUS: 3,
} as const;

/** key containt id of precedeing elemnt id we use in reconciliation during
 * collaboration */
export const PRECEDING_ELEMENT_KEY = "__precedingElement__";

export const ROUGHNESS = {
  architect: 0,
  artist: 1,
  cartoonist: 2,
} as const;

export const STROKE_WIDTH = {
  thin: 1,
  bold: 2,
  extraBold: 4,
} as const;

export const DEFAULT_ELEMENT_PROPS: {
  strokeColor: SchemadrawElement["strokeColor"];
  backgroundColor: SchemadrawElement["backgroundColor"];
  fillStyle: SchemadrawElement["fillStyle"];
  strokeWidth: SchemadrawElement["strokeWidth"];
  strokeStyle: SchemadrawElement["strokeStyle"];
  roughness: SchemadrawElement["roughness"];
  opacity: SchemadrawElement["opacity"];
  locked: SchemadrawElement["locked"];
} = {
  strokeColor: COLOR_PALETTE.black,
  backgroundColor: COLOR_PALETTE.transparent,
  fillStyle: "solid",
  strokeWidth: 1,
  strokeStyle: "solid",
  roughness: ROUGHNESS.architect,
  opacity: 100,
  locked: false,
};

/* Media codec */
export const DATA_TYPE = {
  AVATAR: "avatar",
  IMAGE: "image",
  VIDEO: "video",
  DRAFT: "draft",
};

/* Gender */
export const GENDER = {
  UNKNOWN: 0,
  MALE: 1,
  FEMALE: 2
};

/* error code */
export const ERROR_CODE = {
  INVALID_PARAMETER: 1000,
  DATABASE_ERROR: 1001,
  NOT_AUTHORIZED: 1002,
  SYSTEM_ERROR: 1003
}

// storage
export const OBJECT_STORAGE_URL = {
  DEFAULT_USER_AVATAR: 'https://data.procviz.com/livetoolkit/common/defaultUser.jpg',
}

// API request
export const BACKEND_API_BASE = "https://www.procviz.com"
//export const BACKEND_API_BASE = "http://localhost:8080"

export const BACKEND_API_URL = {
  USER_REGISTER_URL: BACKEND_API_BASE + "/api/user/register",
  USER_LOGIN_URL: BACKEND_API_BASE + "/api/user/login",
  USER_GET_URL: BACKEND_API_BASE + "/api/user/get",
  USER_UPDATE_URL: BACKEND_API_BASE + "/api/user/update",
  USER_AVATAR_URL: BACKEND_API_BASE + "/api/user/avatar",
  USER_CREDIT_URL: BACKEND_API_BASE + "/api/user/credit",
  USER_PASSWORD_URL: BACKEND_API_BASE + "/api/user/password",
  MEDIA_UPLOAD_URL: BACKEND_API_BASE + "/api/asset/create",
  DESIGN_CREATE_URL: BACKEND_API_BASE + "/api/design/create",
  DESIGN_GENERATE_URL: BACKEND_API_BASE + "/api/design/generate",
  DESIGN_DELETE_URL: BACKEND_API_BASE + "/api/design/delete",
  DESIGN_UPDATE_URL: BACKEND_API_BASE + "/api/design/update",
  DESIGN_GET_URL: BACKEND_API_BASE + "/api/design/get",
  DESIGN_SHARED_URL: BACKEND_API_BASE + "/api/design/shared",
  DESIGN_SEARCH_URL: BACKEND_API_BASE + "/api/design/search",
  TEMPLATE_CREATE_URL: BACKEND_API_BASE + "/api/template/create",
  TEMPLATE_GENERATE_URL: BACKEND_API_BASE + "/api/template/generate",
  TEMPLATE_DELETE_URL: BACKEND_API_BASE + "/api/template/delete",
  TEMPLATE_UPDATE_URL: BACKEND_API_BASE + "/api/template/update",
  TEMPLATE_GET_URL: BACKEND_API_BASE + "/api/template/get",
  TEMPLATE_SEARCH_URL: BACKEND_API_BASE + "/api/template/search",
  TEMPLATE_PUBLISH_URL: BACKEND_API_BASE + "/api/template/publish",
  TEMPLATE_LIKE_URL: BACKEND_API_BASE + "/api/template/like",
  TEMPLATE_VIEW_URL: BACKEND_API_BASE + "/api/template/view",
  SCENE_CREATE_URL: BACKEND_API_BASE + "/api/scene/create",
  SCENE_DELETE_URL: BACKEND_API_BASE + "/api/scene/delete",
  SCENE_UPDATE_URL: BACKEND_API_BASE + "/api/scene/update",
  SCENE_GET_URL: BACKEND_API_BASE + "/api/scene/get",
  LIBRARY_CREATE_URL: BACKEND_API_BASE + "/api/library/create",
  LIBRARY_DELETE_URL: BACKEND_API_BASE + "/api/library/delete",
  LIBRARY_UPDATE_URL: BACKEND_API_BASE + "/api/library/update",
  LIBRARY_USE_URL: BACKEND_API_BASE + "/api/library/use",
  LIBRARY_LIKE_URL: BACKEND_API_BASE + "/api/library/like",
  LIBRARY_VIEW_URL: BACKEND_API_BASE + "/api/library/view",
  LIBRARY_GET_URL: BACKEND_API_BASE + "/api/library/get",
  LIBRARY_SEARCH_URL: BACKEND_API_BASE + "/api/library/search",
  POSTER_SEARCH_URL: BACKEND_API_BASE + "/api/poster/search",
  POSTER_GET_URL: BACKEND_API_BASE + "/api/poster/get",
  LLM_TEXT_MERMAID_URL: BACKEND_API_BASE + "/api/llm/text-mermaid",
}


// ali-oss
export const ALI_OSS = {
  REGION: "oss-cn-beijing",
  END_POINT: "oss-cn-beijing.aliyuncs.com",
  ACCESS_KEY_ID: "LTAI5tJc99JF1cX9g8NY5yye",
  ACCESS_KEY_SECRET: "vNmueSIddq0bqevkZT7AhRLFEZMTlh",
  BUCKET: "lazybone-livetoolkit",
  CACHE_URI: "https://data.livetoolkit.cn"
}

// tx-cos
export const TX_COS = {
  REGION: "ap-beijing",
  END_POINT: "https://livetoolkit-1320145461.cos.ap-beijing.myqcloud.com",
  ACCESS_KEY_ID: "AKIDRsBIIJNNCqR6cv1yjGZpshIfpnY6A3i9",
  ACCESS_KEY_SECRET: "JPJ2YrwoSs55rX6UgeQ3nRtAVovqa8Ud",
  BUCKET: "livetoolkit-1320145461",
  CACHE_URI: "https://data.procviz.com/"
}


// time constants (ms)
export const SAVE_TO_LOCAL_STORAGE_TIMEOUT = 300;
export const INITIAL_SCENE_UPDATE_TIMEOUT = 5000;
export const FILE_UPLOAD_TIMEOUT = 300;
export const LOAD_IMAGES_TIMEOUT = 500;
export const SYNC_FULL_SCENE_INTERVAL_MS = 20000;
export const SYNC_BROWSER_TABS_TIMEOUT = 50;
export const CURSOR_SYNC_TIMEOUT = 33; // ~30fps
export const DELETED_ELEMENT_TIMEOUT = 24 * 60 * 60 * 1000; // 1 day

export const FILE_UPLOAD_MAX_BYTES = 3 * 1024 * 1024; // 3 MiB
// 1 year (https://stackoverflow.com/a/25201898/927631)
export const FILE_CACHE_MAX_AGE_SEC = 31536000;

export const WS_EVENTS = {
  SERVER_VOLATILE: "server-volatile-broadcast",
  SERVER: "server-broadcast",
  USER_FOLLOW_CHANGE: "user-follow",
  USER_FOLLOW_ROOM_CHANGE: "user-follow-room-change",
} as const;

export enum WS_SUBTYPES {
  INVALID_RESPONSE = "INVALID_RESPONSE",
  INIT = "SCENE_INIT",
  UPDATE = "SCENE_UPDATE",
  MOUSE_LOCATION = "MOUSE_LOCATION",
  IDLE_STATUS = "IDLE_STATUS",
  USER_VISIBLE_SCENE_BOUNDS = "USER_VISIBLE_SCENE_BOUNDS",
}

export enum WS_SCENE_EVENT_TYPES {
  INIT = "SCENE_INIT",
  UPDATE = "SCENE_UPDATE",
}

export const ALIOSS_STORAGE_PREFIXES = {
  shareLinkFiles: `/livetoolkit/draw/files`,
  sceneSnapshotImages: `/livetoolkit/draw/snapshot`,
  tutorialImages: `/livetoolkit/draw/tutorial`,
  userAvatarImages: `/livetoolkit/draw/avatar`,
  collabFiles: `/livetoolkit/draw/rooms`,
};

export const TXCOS_STORAGE_PREFIXES = {
  shareLinkFiles: `/livetoolkit/draw/files`,
  sceneSnapshotImages: `/livetoolkit/draw/snapshot`,
  tutorialImages: `/livetoolkit/draw/tutorial`,
  userAvatarImages: `/livetoolkit/draw/avatar`,
  collabFiles: `/livetoolkit/draw/rooms`,
};

export const ROOM_ID_BYTES = 10;

export const STORAGE_KEYS = {
  LOCAL_STORAGE_ELEMENTS: "schemadraw",
  LOCAL_STORAGE_APP_STATE: "schemadraw-state",
  LOCAL_STORAGE_COLLAB: "schemadraw-collab",
  LOCAL_STORAGE_LIBRARY: "schemadraw-library",
  LOCAL_STORAGE_THEME: "schemadraw-theme",
  VERSION_DATA_STATE: "version-dataState",
  VERSION_FILES: "version-files",

  IDB_LIBRARY: "schemadraw-library",

  // do not use apart from migrations
  __LEGACY_LOCAL_STORAGE_LIBRARY: "schemadraw-library",
} as const;

export const COOKIES = {
  AUTH_STATE_COOKIE: "excplus-auth",
} as const;

export const isPlusSignedUser = document.cookie.includes(
  COOKIES.AUTH_STATE_COOKIE,
);

export const LIBRARY_SIDEBAR_TAB = "library";

export const DEFAULT_SIDEBAR = {
  name: "default",
  defaultTab: LIBRARY_SIDEBAR_TAB,
} as const;

export const LIBRARY_DISABLED_TYPES = new Set([
  "iframe",
  "embeddable",
  "image",
] as const);

// use these constants to easily identify reference sites
export const TOOL_TYPE = {
  selection: "selection",
  rectangle: "rectangle",
  diamond: "diamond",
  ellipse: "ellipse",
  arrow: "arrow",
  line: "line",
  freedraw: "freedraw",
  text: "text",
  image: "image",
  eraser: "eraser",
  hand: "hand",
  frame: "frame",
  magicframe: "magicframe",
  embeddable: "embeddable",
  laser: "laser",
} as const;

export const EDITOR_LS_KEYS = {
  OAI_API_KEY: "schemadraw-oai-api-key",
  // legacy naming (non)scheme
  MERMAID_TO_SCHEMADRAW: "mermaid-to-schemadraw",
  PUBLISH_LIBRARY: "publish-library-data",
} as const;

export const ALLOWED_PASTE_MIME_TYPES = ["text/plain", "text/html"] as const;

/**
 * not translated as this is used only in public, stateless API as default value
 * where filename is optional and we can't retrieve name from app state
 */
export const DEFAULT_FILENAME = "Untitled";

export const STATS_PANELS = { generalStats: 1, elementProperties: 2 } as const;

export const MIN_WIDTH_OR_HEIGHT = 1;